<template>
  <v-dialog v-model="localModelValue" max-width="736">
    <v-card class="change-email-modal">
      <div class="modal-content-wrapper">
        <!-- Step 1: Email Input -->
        <div v-if="currentStep === 1" class="modal-step">
          <div class="modal-header-section">
            <h2 class="modal-main-title">Email for Sign In</h2>
            <p class="modal-subtitle">
              Enter your email to continue setting up your account.
            </p>
          </div>

          <div class="email-field-group">
            <label class="modal-field-label">Email</label>
            <BaseInput
              v-model="email"
              class="modal-email-input"
              placeholder="emilio@rocketmail.com"
            />
          </div>

          <div class="modal-actions-container">
            <ActionButton
              variant="primary"
              class="modal-btn modal-btn-continue"
              :class="{ 'modal-btn-disabled': !isEmailValid }"
              :disabled="!isEmailValid || isLoading"
              :loading="isLoading"
              @click="handleEmailContinue"
            >
              Continue
            </ActionButton>
          </div>
        </div>

        <!-- Step 2: OTP Input -->
        <div v-if="currentStep === 2" class="modal-step">
          <div class="modal-header-section">
            <h2 class="modal-main-title">Email for Sign In</h2>
            <p class="modal-subtitle">Input your OTP below</p>
          </div>

          <OTPInput
            ref="otpInputRef"
            :isLoading="isVerifyingOtp"
            :showResend="true"
            :modalMode="true"
            @verify="handleOtpVerify"
            @resend="handleOtpResend"
            @validate="handleOtpValidate"
          />
        </div>

        <!-- Step 3: Password Setup -->
        <div v-if="currentStep === 3" class="modal-step">
          <div class="modal-header-section">
            <h2 class="modal-main-title">Email for Sign In</h2>
            <p class="modal-subtitle">Set up your password</p>
          </div>

          <div class="password-fields-section">
            <div class="password-field-group">
              <label class="modal-field-label">New Password</label>
              <div class="password-input-container">
                <BaseInput
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="modal-password-input"
                  @input="validatePasswordInput"
                />
              </div>
              <PasswordValidationList
                v-if="password"
                :password="password"
                :validations="validations"
              />
            </div>

            <div class="password-field-group">
              <label class="modal-field-label">Confirm New Password</label>
              <div class="password-input-container">
                <BaseInput
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="modal-password-input"
                  @input="validateConfirmPassword"
                  :error="isConfirmPasswordError"
                  :errorMessage="confirmPasswordErrorMessage"
                  :showInputError="false"
                />
              </div>
            </div>
          </div>

          <div class="modal-actions-container">
            <ActionButton
              variant="primary"
              class="modal-btn modal-btn-save"
              :class="{ 'modal-btn-disabled': !canSave }"
              :disabled="!canSave || isLoading"
              :loading="isLoading"
              @click="handleSave"
            >
              Save
            </ActionButton>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
/* eslint-disable */
import { computed, ref, watch, defineProps, defineEmits, inject } from "vue";
import { validatePassword } from "@/utils";
import PasswordValidationList from "../PasswordValidationList.vue";
import BaseInput from "../BaseInput.vue";
import ActionButton from "../common/ActionButton.vue";
import OTPInput from "../OTPInput.vue";
import userService from "@/services/userService";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "save"]);

const localModelValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const currentStep = ref(1);
const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const isLoading = ref(false);
const isOtpNeeded = ref(false);
const otpVerified = ref(false);
const verifiedOtp = ref("");
const isVerifyingOtp = ref(false);
const otpInputRef = ref(null);
const notify = inject("notify");

const isConfirmPasswordError = ref(false);
const confirmPasswordErrorMessage = ref("");
const validations = ref({
  lowercase: false,
  uppercase: false,
  specialChar: false,
  length: false,
});

const reset = () => {
  currentStep.value = 1;
  email.value = "";
  password.value = "";
  confirmPassword.value = "";
  showPassword.value = false;
  showConfirmPassword.value = false;
  isOtpNeeded.value = false;
  otpVerified.value = false;
  verifiedOtp.value = "";
  isVerifyingOtp.value = false;
  if (otpInputRef.value) {
    otpInputRef.value.reset();
  }
  isValidated.value = false;
  validationResult.value = null;
  isConfirmPasswordError.value = false;
  confirmPasswordErrorMessage.value = "";
  validations.value = {
    lowercase: false,
    uppercase: false,
    specialChar: false,
    length: false,
  };
};

const isEmailValid = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email.value.trim().length > 0 && emailRegex.test(email.value);
});

const handleEmailContinue = async () => {
  if (!isEmailValid.value) {
    return;
  }

  isLoading.value = true;

  try {
    const response = await userService.requestConnectRegulerEmail(email.value);

    if (response?.data?.data?.is_otp_needed) {
      isOtpNeeded.value = true;
      currentStep.value = 2; // Show OTP step
      notify.success("OTP has been sent to your email");
    } else {
      isOtpNeeded.value = false;
      currentStep.value = 3; // Skip OTP, go directly to password
    }
  } catch (error) {
    console.error("Failed to request connect email:", error);
    notify.error(
      error?.response?.data?.message || "Failed to send OTP. Please try again."
    );
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      reset();
    }
  }
);

const isValidated = ref(false);
const validationResult = ref(null);

const handleOtpValidate = async (otp) => {
  if (!otp || otp.length !== 6) {
    return;
  }

  // Don't validate if already verifying
  if (isVerifyingOtp.value) {
    return;
  }

  // If already validated with same code, don't validate again
  if (isValidated.value && validationResult.value?.otp === otp) {
    if (otpInputRef.value) {
      if (validationResult.value.isValid) {
        otpInputRef.value.setValid();
      } else {
        otpInputRef.value.setError(
          validationResult.value.error || "Invalid code"
        );
      }
    }
    return;
  }

  // Clear previous validation if OTP changed
  if (isValidated.value && validationResult.value?.otp !== otp) {
    isValidated.value = false;
    validationResult.value = null;
  }

  try {
    await userService.verifyConnectRegulerEmail(email.value, otp);

    // Store validation result
    isValidated.value = true;
    validationResult.value = {
      otp,
      isValid: true,
    };

    // Update OTP component to show valid state
    if (otpInputRef.value) {
      otpInputRef.value.setValid();
    }
  } catch (error) {
    let errorMsg = "Invalid code";
    if (error?.response?.data?.message) {
      errorMsg = error.response.data.message;
    }

    // Store validation result as invalid
    isValidated.value = true;
    validationResult.value = {
      otp,
      isValid: false,
      error: errorMsg,
    };

    // Update OTP component to show error state
    if (otpInputRef.value) {
      otpInputRef.value.setError(errorMsg);
    }
  }
};

const handleOtpVerify = async (otp) => {
  if (!otp || otp.length !== 6) {
    return;
  }

  isVerifyingOtp.value = true;

  try {
    // If already validated and valid, we can proceed
    if (
      isValidated.value &&
      validationResult.value?.otp === otp &&
      validationResult.value?.isValid
    ) {
      // Logic already performed in handleOtpValidate, just proceed
    } else {
      // Otherwise, verify now
      await userService.verifyConnectRegulerEmail(email.value, otp);

      // Store validation result
      isValidated.value = true;
      validationResult.value = {
        otp,
        isValid: true,
      };
    }

    otpVerified.value = true;
    verifiedOtp.value = otp; // Store the verified OTP
    if (otpInputRef.value) {
      otpInputRef.value.setValid();
    }
    currentStep.value = 3; // Go to password step
    notify.success("OTP verified successfully");
  } catch (error) {
    console.error("Failed to verify OTP:", error);
    const errorMessage =
      error?.response?.data?.message || "Invalid OTP. Please try again.";

    // Store validation result as invalid
    isValidated.value = true;
    validationResult.value = {
      otp,
      isValid: false,
      error: errorMessage,
    };

    if (otpInputRef.value) {
      otpInputRef.value.setError(errorMessage);
    }
    notify.error(errorMessage);
  } finally {
    isVerifyingOtp.value = false;
  }
};

const handleOtpResend = async () => {
  try {
    await userService.requestConnectRegulerEmail(email.value);
    if (otpInputRef.value) {
      otpInputRef.value.reset();
      otpInputRef.value.setResendError("");
    }
    notify.success("OTP has been resent to your email");
  } catch (error) {
    console.error("Failed to resend OTP:", error);
    const errorMessage =
      error?.response?.data?.message ||
      "Failed to resend OTP. Please try again.";
    if (otpInputRef.value) {
      otpInputRef.value.setResendError(errorMessage);
    }
    notify.error(errorMessage);
  }
};

const validatePasswordInput = () => {
  const result = validatePassword(password.value);
  validations.value = result.validations;

  if (confirmPassword.value) {
    validateConfirmPassword();
  }
};

const validateConfirmPassword = () => {
  if (!confirmPassword.value) {
    isConfirmPasswordError.value = false;
    confirmPasswordErrorMessage.value = "";
    return;
  }

  if (password.value !== confirmPassword.value) {
    isConfirmPasswordError.value = true;
    confirmPasswordErrorMessage.value = "Password doesn’t match";
  } else {
    isConfirmPasswordError.value = false;
    confirmPasswordErrorMessage.value = "";
  }
};

const isPasswordValid = computed(() => {
  return Object.values(validations.value).every((v) => v);
});

const canSave = computed(() => {
  return (
    isPasswordValid.value &&
    password.value === confirmPassword.value &&
    password.value.length > 0
  );
});

const handleSave = async () => {
  if (!canSave.value) {
    return;
  }

  isLoading.value = true;

  try {
    if (isOtpNeeded.value && otpVerified.value) {
      // Use OTP flow endpoint
      const otp = verifiedOtp.value || otpInputRef.value?.getOTP() || "";
      const payload = {
        email: email.value,
        otp: otp,
        password: password.value,
        confirm_password: confirmPassword.value,
      };

      await userService.connectOtpRegulerEmail(payload);
    } else {
      // Use regular flow endpoint (no OTP)
      const payload = {
        email: email.value,
        password: password.value,
        confirm_password: confirmPassword.value,
      };

      await userService.connectRegulerEmail(payload);
    }

    notify.success("Profile has been changed");
    emit("save", {
      email: email.value,
      password: password.value,
      confirmPassword: confirmPassword.value,
    });
    localModelValue.value = false;
  } catch (error) {
    console.error("Failed to connect email:", error);
    notify.error(
      error?.response?.data?.message || "Failed to connect email account"
    );
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.change-email-modal {
  border-radius: 16px !important;
  overflow: visible;
  box-shadow: none;
}

.modal-content-wrapper {
  padding: 32px;
}

.modal-step {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.modal-header-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-main-title {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xxl);
  font-weight: 700;
  line-height: 36px;
  color: #3b4648;
  margin: 0;
}

.modal-subtitle {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  margin: 0;
}

.email-field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.otp-field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.modal-field-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: #3b4648;
  margin: 0;
}

.modal-email-input {
  width: 100%;
}

.modal-email-input :deep(.input-main) {
  height: 56px;
  width: 100%;
  border: 1px solid #6c737f;
  background-color: #ffffff;
  color: #3b4648;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.024px;
}

.modal-email-input :deep(.input-main::placeholder) {
  color: #98a2b3;
}

.otp-inputs {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  width: 100%;
}

.otp-input {
  height: 64px;
  width: 100%;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background-color: white;
  text-align: center;
  font-family: "League Spartan", sans-serif;
  font-size: 36px;
  font-weight: 500;
  line-height: 44px;
  color: var(--primary-blue);
  padding: 4px 16px;
  outline: none;
  transition: all 0.2s;
}

.otp-input::placeholder {
  color: #98a2b3;
}

.otp-input:focus {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 2px rgba(0, 61, 106, 0.1);
}

.otp-input-error {
  border-color: #c21d1d;
}

.otp-hint-text {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.025px;
  color: #6c737f;
  margin: 0;
}

.otp-error-text {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.025px;
  color: #c21d1d;
  margin: 0;
}

.modal-actions-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.modal-btn {
  height: 40px;
  min-width: 100px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.014px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.modal-btn-continue,
.modal-btn-save {
  background-color: var(--primary-blue);
  color: #ffffff;
}

.modal-btn-continue:hover:not(:disabled),
.modal-btn-save:hover:not(:disabled) {
  background-color: #002d4f;
}

.modal-btn-disabled {
  background-color: #dfe2e7 !important;
  color: #cccccc !important;
  cursor: not-allowed;
}

.password-fields-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.password-field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.password-input-container {
  position: relative;
  width: 100%;
}

.modal-password-input {
  width: 100%;
}

.modal-password-input :deep(.input-main) {
  padding-right: 48px !important;
  width: 100%;
}

.password-toggle-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle-btn img {
  width: 24px;
  height: 24px;
}

@media (max-width: 768px) {
  .modal-content-wrapper {
    padding: 24px;
  }

  .modal-main-title {
    font-size: var(--fs-large);
    line-height: 28px;
  }

  .modal-field-label {
    font-size: 18px;
    line-height: 24px;
  }

  .otp-input {
    height: 56px;
    font-size: var(--fs-xxl);
  }
}
</style>
